import { getRelativePath } from "@/utils/basePath";
import { getArtworkIdsByRoute, getUnroutedArtworks } from "./localCollection";

/**
 * Per-route artwork configuration + the sign ↔ artwork binding.
 *
 * The whole boardwalk ships as a single Creator *collection*
 * (`COLLECTION_DATA_SOURCE`), cached locally by `pnpm run cache:collection`
 * and loaded once at boot (see `src/data/data.ts`). The collection is the
 * canonical source: it decides which artworks exist and which sign each one
 * belongs to.
 *
 * Each of the five interpretive signs binds to one artwork by ROUTE, not by
 * artwork id. The cache script decodes the QR printed on each artwork and
 * records the route it points at, so the binding survives the artworks being
 * deleted and rebuilt in Creator — which has already happened once, changing
 * every id and every name in one go. Nothing here needs editing when that
 * recurs; re-running the cache script is enough.
 *
 * The active route is resolved at boot from `window.location.pathname` so the
 * data layer / UI know which sign belongs to the current route.
 */

/** The five interpretive signs, in brief order (Interp Sign 1–5). */
export type SignKey =
  | "seahorse"
  | "microbat"
  | "black-cockatoo"
  | "oysters"
  | "crab";

export interface ArtworkConfig {
  /** The interpretive sign this artwork belongs to. */
  sign: SignKey;
  /** Route path that activates this artwork. */
  route: string;
  /**
   * Real Creator artwork id — matches an artwork in the collection payload and
   * the 8thwall image-target name.
   */
  artworkId: string;
  /** Creature name; mirrors the collection artwork name (see module doc). */
  name: string;
}

/**
 * The "Kangaroo Point" Creator collection: all five artworks in one payload.
 * Loaded once at boot; QR switches read from it in memory (no refetch).
 */
export const COLLECTION_DATA_SOURCE =
  "https://launch.eyejack.io/Collection-8dbdcbe1-f0f0-4616-9251-437a45e64e57/json";

/**
 * The five signs. Identity, order and copy live here; the artwork each one
 * points at does NOT.
 *
 * Artwork ids used to be written out below, and that broke the moment the
 * collection was deleted and rebuilt in Creator — every id changed at once and
 * every route went dead. Routes are the stable half of the binding, because
 * they are printed on the physical signs, so the artwork id is now resolved
 * from the cached collection by route (see `ARTWORKS`).
 */
const SIGNS: Omit<ArtworkConfig, "artworkId">[] = [
  { sign: "seahorse", route: "/seahorse", name: "Seahorse" },
  { sign: "microbat", route: "/microbat", name: "Microbat" },
  { sign: "black-cockatoo", route: "/cockatoo", name: "Black-Cockatoo" },
  { sign: "oysters", route: "/oysters", name: "Oysters" },
  { sign: "crab", route: "/crab", name: "Crab" },
];

/**
 * Single source of truth for the sign ↔ artwork binding, assembled at boot:
 * the sign list above, joined to the cached collection on the route decoded
 * from each artwork's own QR code.
 *
 * A rebuild in Creator therefore needs no code change — re-run
 * `pnpm run cache:collection` and the new ids are picked up. A sign whose
 * artwork is missing from the cache resolves to an empty id and fails loudly
 * at `requireLocalTarget` in `data.ts`, naming the sign.
 */
export const ARTWORKS: ArtworkConfig[] = (() => {
  const idByRoute = getArtworkIdsByRoute();
  const bound = SIGNS.map((s) => ({ ...s, artworkId: idByRoute[s.route] ?? "" }));

  if (import.meta.env.DEV) {
    const missing = bound.filter((a) => !a.artworkId);
    if (missing.length) {
      console.warn(
        `[artworks] no cached artwork for ${missing.map((a) => a.route).join(", ")}. ` +
          `The cache binds on the QR printed on each artwork; re-run \`pnpm run cache:collection\`.`,
      );
    }
    const spare = getUnroutedArtworks();
    if (spare.length) {
      console.warn(
        `[artworks] cached artwork with no readable QR, so bound to no sign: ` +
          spare.map((a) => `${a.name} (${a.id})`).join(", "),
      );
    }
  }

  return bound;
})();

/** Sign order (brief order), derived from {@link ARTWORKS}. */
export const SIGN_ORDER: SignKey[] = ARTWORKS.map((a) => a.sign);

/** Short, human-readable creature name per sign (nav labels, dev pickers). */
export const SIGN_LABEL: Record<SignKey, string> = Object.fromEntries(
  ARTWORKS.map((a) => [a.sign, a.name]),
) as Record<SignKey, string>;

/** Explicit sign → real Creator artwork id map, derived from {@link ARTWORKS}. */
export const SIGN_TO_ARTWORK_ID: Record<SignKey, string> = Object.fromEntries(
  ARTWORKS.map((a) => [a.sign, a.artworkId]),
) as Record<SignKey, string>;

/**
 * Legacy per-artwork Launch JSON URL. The app now boots from the collection
 * ({@link COLLECTION_DATA_SOURCE}); this remains for the dev tooling that
 * previews a single artwork's target/QR in isolation.
 */
export const launchUrlFor = (artworkId: string): string =>
  `https://launch.eyejack.io/${artworkId}/json`;

export const DEFAULT_ARTWORK_ROUTE = ARTWORKS[0]!.route;

/**
 * Runtime override for the active artwork.
 *
 * At boot the active artwork is resolved from the URL. Once the QR scanner
 * switches artworks mid-session it sets this override so every later
 * `getActiveArtworkConfig()` call (facts, names, target generation) reflects
 * the scanned artwork rather than the boot URL.
 */
let activeArtworkOverride: ArtworkConfig | null = null;

/**
 * Per-TAB memory of the active artwork.
 *
 * The sign lives here rather than in the URL so the address bar can be cleared
 * back to the site root (see {@link captureArtworkFromUrl}) — a visitor who
 * shares what's in their address bar hands over the microsite, not whichever
 * sign they happened to be standing at. `sessionStorage`, not `localStorage`,
 * for exactly that reason: it survives a refresh in this tab and nothing else.
 * A fresh tab, a new device, or the shared link opening anywhere else all start
 * with no sign and land on the microsite.
 */
const ACTIVE_ARTWORK_KEY = "ej-active-artwork";

const readStoredArtworkId = (): string | null => {
  try {
    return sessionStorage.getItem(ACTIVE_ARTWORK_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
};

const storeArtworkId = (artworkId: string): void => {
  try {
    sessionStorage.setItem(ACTIVE_ARTWORK_KEY, artworkId);
  } catch {
    /* storage disabled — the session just won't survive a refresh. */
  }
};

/**
 * Mark an artwork as active by id (used by the QR switch flow). Returns the
 * matched config, or undefined if the id isn't a configured artwork.
 */
export const setActiveArtworkById = (
  artworkId: string,
): ArtworkConfig | undefined => {
  const match = ARTWORKS.find((a) => a.artworkId === artworkId);
  if (match) {
    activeArtworkOverride = match;
    storeArtworkId(match.artworkId);
  }
  return match;
};

/**
 * Whether this tab has a sign to show. Drives the site root: with a sign it
 * resumes that experience, without one it is the microsite.
 */
export const hasActiveArtwork = (): boolean => activeArtworkOverride !== null;

/**
 * Boot step: take the sign out of the URL and into this tab's memory.
 *
 * A scan arrives at "/crab". We record that sign, then `replaceState` the
 * address bar back to the site root, so what the visitor can copy, share or
 * bookmark is the microsite. Refreshing still lands on their sign because the
 * id is in `sessionStorage`, which a reload keeps and a shared link never
 * carries.
 *
 * `replaceState` rather than a router navigation: it rewrites the entry in
 * place, so there is no history entry pointing back at the sign URL and no
 * re-render. It fires no `popstate`, so preact-router doesn't react to it —
 * which is why `AppRouter` decides the root route from {@link hasActiveArtwork}
 * rather than from the path alone.
 *
 * Must run before the UI renders, or the router sees the sign path and mounts
 * the artwork route from the URL instead — call it from the app entry.
 */
export const captureArtworkFromUrl = (): void => {
  if (typeof window === "undefined") return;

  const { pathname, search } = window.location;
  const fromUrl = artworkForPathname(pathname);

  if (fromUrl) {
    activeArtworkOverride = fromUrl;
    storeArtworkId(fromUrl.artworkId);

    // Root of THIS deploy (keeps the launch-v4 sub-path), and keep any query
    // string — it can carry things this app doesn't own.
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    window.history.replaceState(
      window.history.state,
      "",
      `${base}/${search}`,
    );
    return;
  }

  // No sign in the URL: restore the one this tab was already on, if any. This
  // is the refresh case — the address bar has been root since the first load.
  const storedId = readStoredArtworkId();
  if (storedId) {
    const stored = ARTWORKS.find((a) => a.artworkId === storedId);
    if (stored) activeArtworkOverride = stored;
  }
};

/**
 * Returns the active artwork: the runtime override if a switch has occurred,
 * otherwise the config matching the current location, otherwise the default.
 * The pathname lookup is suffix-based so it works regardless of base URL /
 * launch-v4 prefix (e.g. "/bp1/microbat").
 */
export const getActiveArtworkConfig = (): ArtworkConfig => {
  if (activeArtworkOverride) return activeArtworkOverride;

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";

  return artworkForPathname(pathname) ?? ARTWORKS[0]!;
};

/**
 * The sign a pathname names, by suffix so it works under any deploy base path
 * ("/bp1/microbat").
 *
 * The /dev tools are exempt: their own paths can end in a sign route
 * (/dev/targets/seahorse), and reading that as a scan would capture the sign
 * and send the tool back to the site root.
 */
const artworkForPathname = (pathname: string): ArtworkConfig | undefined =>
  getRelativePath(pathname).startsWith("/dev")
    ? undefined
    : ARTWORKS.find((a) => pathname.endsWith(a.route));

export const getArtworkConfigByRoute = (
  route: string,
): ArtworkConfig | undefined => ARTWORKS.find((a) => a.route === route);

/**
 * Resolve a scanned QR payload to an artwork config, or undefined if the
 * payload isn't one of our artworks. Tolerant of the shapes a QR code might
 * encode:
 *   - a full URL            "https://site.com/microbat"  (with optional #hash)
 *   - an absolute path      "/microbat"  or  "/bp1/microbat"
 *   - a bare route segment  "microbat"
 *   - a raw artwork id      "Artwork-8ed18442-..."
 */
export const resolveArtworkFromQrPayload = (
  payload: string,
): ArtworkConfig | undefined => {
  const raw = (payload ?? "").trim();
  if (!raw) return undefined;

  // Reduce a full URL to "<pathname>/<hash-without-#>" so "#/microbat" and
  // "/microbat" both resolve. Non-URL payloads fall through unchanged.
  let candidate = raw;
  try {
    const url = new URL(raw);
    candidate = `${url.pathname}/${url.hash.replace(/^#\/?/, "")}`;
  } catch {
    /* not a full URL — treat as a path or fragment */
  }

  const normalized = candidate.replace(/\/+$/, "");

  // 1) Route suffix match — handles full URLs and prefixed paths.
  const byRoute = ARTWORKS.find((a) => normalized.endsWith(a.route));
  if (byRoute) return byRoute;

  // 2) Last path segment match — handles bare "microbat".
  const segments = normalized.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  if (lastSegment) {
    const bySegment = ARTWORKS.find(
      (a) => a.route.replace(/^\//, "") === lastSegment,
    );
    if (bySegment) return bySegment;
  }

  // 3) Raw artwork id match.
  return ARTWORKS.find(
    (a) => a.artworkId === raw || segments.includes(a.artworkId),
  );
};
